import { FlightResponse } from '@/types/benefits/flight/FlightResponse';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import StyledContainer from '../styled/StyledContainer';
import { useLocation, useNavigate } from 'react-router';
import StyledFormHeaderWithBreadcrumb from '../styled/StyledFormHeaderWithBreadcrumb';
import { formatBenefitCode } from '@/utils/displayCodes';
import { PATHS } from '@/constants/Paths';
import { buildBenefitsListSearch } from '@/utils/searchParams/buildBenefitsListSearch';
import { getListSearchFromLocation } from '@/utils/searchParams/getListSearchFromLocation';
import StyledButton from '../styled/StyledButton';
import StyledButtonLoader from '../styled/StyledButtonLoader';
import { SupplierStatus } from '@/types/suppliers/SupplierStatus';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import EditIcon from '@/assets/icons/edit-01.svg?react';
import CopyIcon from '@/assets/icons/copy-03.svg?react';
import SaveIcon from '@/assets/icons/save-02.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import HorizontalLine from '../ui/HorizontalLine';
import { useDuplicateFlight, useUpdateFlight } from '@/hooks/useFlight';
import { CreateFlightWithTranslationsType } from '@/types/benefits/flight/CreateFlight.type';
import clsx from 'clsx';
import BenefitDeletionModal from '@/components/benefits/BenefitDeletionModal';
import ModalDuplication from '../ui/ModalDuplication';
import { FLIGHT_DUPLICATION_FIELDS } from '@/constants/benefitFields';
import BenefitTypeCell from '../benefits/BenefitTypeCell';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { SupportedLocale } from '@/types/supported-locale.type';
import { loadFlightDefaultValues } from '@/utils/benefits/flights/loadFlightDefaultValues';
import {
  FlightFormDataWithTranslations,
  FlightSchemaWithTranslations,
} from '@/i18n/translation-type-utils';
import { useTranslateFields } from '@/hooks/useTranslate';
import LanguageSwitchWithTranslateBtn from '../translation/LanguageSwitchWithTranslateBtn';
import StyledStepper from '../styled/StyledStepper';
import { ArrowRightIcon } from 'lucide-react';
import { GENERAL_BENEFITS_STEPS } from '@/constants/steppers/generalBenefitsSteps';
import BenefitPricingSummary from '../benefits/BenefitPricingSummary';
import FlightConfiguration from './FlightConfiguration';
import { MarginRateSchema } from '@/types/common/margin-rate.schema';

interface ViewFlightFormProps {
  flight: FlightResponse;
  suppliers?: {
    id: string;
    name: string;
    currency: string;
  }[];
  language: SupportedLocale;
  setLanguage: React.Dispatch<React.SetStateAction<SupportedLocale>>;
}

type SubmitFormType = (
  isDraft?: boolean,
) => SubmitHandler<FlightFormDataWithTranslations>;

const ViewFlightForm = ({
  flight,
  suppliers,
  language,
  setLanguage,
}: ViewFlightFormProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const [isEditEnabled, setIsEditEnabled] = useState(false);

  const methods = useForm<FlightFormDataWithTranslations>({
    mode: 'onChange',
    resolver: zodResolver(FlightSchemaWithTranslations),
    defaultValues: loadFlightDefaultValues(flight),
  });

  const {
    control,
    reset,
    setValue,
    formState: { isValid, errors },
    getValues,
    handleSubmit,
  } = methods;

  const currentName = useWatch({ control, name: 'name' });
  const selectedSupplier = useWatch({ control, name: 'supplier' });
  const selectedSourceCountry = useWatch({
    control,
    name: 'sourceCountry',
  });
  const selectedDestinationCountry = useWatch({
    control,
    name: 'destinationCountry',
  });

  const { mutateAsync: updateFlight } = useUpdateFlight(language);
  const { mutateAsync: duplicateFlight } = useDuplicateFlight();
  const [isDeletionModalOpen, setIsDeletionModalOpen] = useState(false);
  const [isDuplicationModalOpen, setIsDuplicationModalOpen] = useState(false);

  const errorsLength = useMemo(() => Object.keys(errors).length, [errors]);

  const { translateFields, isTranslating } = useTranslateFields({
    targetLanguage: language,
    control,
    setValue,
    fields: ['benefit.name', 'shortDescription', 'longDescription'] as const,
  });

  const [currStep, setCurrStep] = useState(0);

  const { toastAsync } = useToast();

  const submitForm: SubmitFormType =
    (isDraft = true) =>
    async (data) => {
      const parsedMarginRate = MarginRateSchema.safeParse(data.marginRate);
      const flightData: Partial<CreateFlightWithTranslationsType> = {
        name: data.name,
        supplierId: data.supplier.value,
        currency: data.currency.value,
        marginRate: parsedMarginRate.success
          ? parsedMarginRate.data
          : undefined,
        sourceCountry: data.sourceCountry?.value,
        sourceCity: data.sourceCity?.value,
        sourceAirport: data.sourceAirport?.value,
        destinationCountry: data.destinationCountry?.value,
        destinationCity: data.destinationCity?.value,
        destinationAirport: data.destinationAirport?.value,
        shortDescription: data.shortDescription,
        longDescription: data.longDescription,
        specialTarif: data.customTarif === 'true',
        childAgeFrom: data.childAgeFrom,
        childAgeTo: data.childAgeTo,
        pricings: data.tarifs.map((tarif) => ({
          name: tarif.name,
          periods: tarif.periods.map((period) => ({
            startDate: period.startDate,
            endDate: period.endDate,
            applicationDays: period.applicationDays,
          })),
          pricePartitions: tarif.pricePartitions.map((partition) => ({
            price: Number(partition.price),
            from: 0,
            to: 0,
            specialPrice: partition.specialPrice
              ? Number(partition.specialPrice)
              : undefined,
          })),
        })),
        status: isDraft ? undefined : SupplierStatus.PUBLISHED,
        translations: data.translations,
      };
      await toastAsync(
        updateFlight({ benefitId: flight.benefitId, data: flightData }),
        { success: t('benefits.benefitUpdatedSuccess') },
      );
      if (!isDraft)
        navigate(
          PATHS.home.benefits.list +
            getListSearchFromLocation(location, buildBenefitsListSearch),
        );
    };

  return (
    <FormProvider {...methods}>
      <StyledContainer>
        <StyledFormHeaderWithBreadcrumb
          title={currentName || flight.benefit.name}
          code={formatBenefitCode(flight.benefit.code)}
          label={<BenefitTypeCell type={flight.benefit.type} />}
          breadcrumb={
            <StyledButton
              variant="link"
              title={t('common.backToList')}
              className="text-sm text-[#3D3D3D] pl-0"
              onClick={() =>
                navigate(
                  PATHS.home.benefits.list +
                    getListSearchFromLocation(
                      location,
                      buildBenefitsListSearch,
                    ),
                )
              }
              icon={<ArrowLeftIcon />}
            />
          }
        >
          <StyledButton
            title={t('common.delete')}
            variant="outlined"
            className="font-semibold gap-1 px-3 py-2 text-gray-800 border-gray-300"
            onClick={() => setIsDeletionModalOpen(true)}
          />
          <StyledButton
            variant="outlined"
            title={t('common.duplicate')}
            className="font-semibold gap-1 px-3 py-2"
            onClick={() => setIsDuplicationModalOpen(true)}
            icon={<CopyIcon className="w-5 h-5 stroke-brand-600" />}
          />
          {!isEditEnabled ? (
            <StyledButton
              title={t('common.edit')}
              onClick={() => setIsEditEnabled(true)}
              className="font-semibold gap-1 px-3 py-2"
              icon={<EditIcon className="w-5 h-5 stroke-white" />}
            />
          ) : (
            <>
              <StyledButton
                title={t('common.cancel')}
                onClick={() => {
                  setIsEditEnabled(false);
                  reset();
                }}
                className="font-semibold gap-1 px-3 py-2"
              />
              <StyledButtonLoader
                title={t('common.save')}
                successTitle={t('common.draftSaved')}
                onClick={
                  flight.benefit.status === SupplierStatus.DRAFT
                    ? () => {
                        setIsEditEnabled(false);
                        submitForm(true)(getValues());
                      }
                    : handleSubmit(submitForm(true))
                }
                disabled={
                  flight.benefit.status === SupplierStatus.DRAFT
                    ? currentName == undefined ||
                      selectedSupplier == undefined ||
                      selectedSourceCountry == undefined ||
                      selectedDestinationCountry == undefined
                    : !isValid
                }
                className="font-semibold gap-1 px-3 py-2"
                icon={<SaveIcon className="w-5 h-5" />}
              />
            </>
          )}
        </StyledFormHeaderWithBreadcrumb>
        <StyledStepper
          activeStep={currStep}
          steps={getTranslatedConst(GENERAL_BENEFITS_STEPS)}
          setStep={(step: number) => setCurrStep(step)}
        />
        <HorizontalLine />
        <LanguageSwitchWithTranslateBtn
          translateFields={() =>
            translateFields({ translateToAllLocales: true })
          }
          isTranslating={isTranslating}
          language={language}
          setLanguage={setLanguage}
          hideTranslateBtn={!isEditEnabled}
        />
        {currStep === 0 ? (
          <>
            <FlightConfiguration
              control={control}
              suppliers={suppliers}
              isEditEnabled={isEditEnabled}
              language={language}
              translateFields={(fields) =>
                translateFields({ fieldsToTranslate: fields })
              }
              isTranslating={isTranslating}
              errorsLength={errorsLength}
            />
            {isEditEnabled && (
              <div className="flex flex-row-reverse p-6">
                <StyledButtonLoader
                  title={t('common.save')}
                  successTitle={t('common.draftSaved')}
                  onClick={
                    flight.benefit.status === SupplierStatus.DRAFT
                      ? () => {
                          setIsEditEnabled(false);
                          submitForm(true)(getValues());
                        }
                      : handleSubmit(submitForm(true))
                  }
                  disabled={
                    flight.benefit.status === SupplierStatus.DRAFT
                      ? currentName == undefined ||
                        selectedSupplier == undefined ||
                        selectedSourceCountry == undefined ||
                        selectedDestinationCountry == undefined
                      : !isValid
                  }
                  className="font-semibold gap-1 px-3 py-2"
                  icon={<SaveIcon className="w-5 h-5" />}
                />
              </div>
            )}
            {flight.benefit.status === SupplierStatus.DRAFT &&
              isEditEnabled && (
                <div className="flex flex-row-reverse p-6">
                  <StyledButtonLoader
                    title={t('flights.publishFlight')}
                    className="font-semibold gap-1 px-3 py-2"
                    successTitle={t('common.publishedSuccess')}
                    hasSucceeded={!errorsLength}
                    errorTitle={t('common.checkRequiredFields')}
                    onClick={handleSubmit(submitForm(false))}
                    icon={
                      <PlusIcon
                        className={clsx('w-5 h-5', {
                          'fill-white': isValid,
                          'fill-gray-400': !isValid,
                        })}
                      />
                    }
                  />
                </div>
              )}
            <div className="flex flex-row-reverse px-6 pb-6 md:w-fit md:sticky md:left-full md:bottom-0">
              <StyledButton
                title={t('common.pricingSummary')}
                icon={<ArrowRightIcon className="w-5 h-5" />}
                iconPosition="right"
                className="font-semibold gap-1 px-3 py-2"
                onClick={() => {
                  setCurrStep(1);
                  window.scrollTo(0, 0);
                }}
              />
            </div>
          </>
        ) : (
          <>
            <BenefitPricingSummary control={control} />
            {isEditEnabled && (
              <div className="flex flex-row-reverse px-6 pb-6">
                <StyledButtonLoader
                  title={t('common.save')}
                  successTitle={t('common.draftSaved')}
                  onClick={
                    flight.benefit.status === SupplierStatus.DRAFT
                      ? () => {
                          submitForm(true)(getValues());
                        }
                      : handleSubmit(submitForm(true))
                  }
                  disabled={
                    flight.benefit.status === SupplierStatus.DRAFT
                      ? currentName == undefined ||
                        selectedSupplier == undefined ||
                        selectedSourceCountry == undefined ||
                        selectedDestinationCountry == undefined
                      : !isValid
                  }
                  className="font-semibold gap-1 px-3 py-2"
                  icon={<SaveIcon className="w-5 h-5" />}
                />
              </div>
            )}
            {flight.benefit.status === SupplierStatus.DRAFT &&
              isEditEnabled && (
                <div className="flex flex-row-reverse p-6 md:sticky md:bottom-0 z-10 bg-white">
                  <StyledButtonLoader
                    title={t('flights.publishFlight')}
                    className="font-semibold gap-1 px-3 py-2"
                    onClick={handleSubmit(submitForm(false))}
                    successTitle={t('common.publishedSuccess')}
                    hasSucceeded={!errorsLength}
                    errorTitle={t('common.checkRequiredFields')}
                    icon={
                      <PlusIcon
                        className={clsx('w-5 h-5', {
                          'fill-white': isValid,
                          'fill-gray-400': !isValid,
                        })}
                      />
                    }
                    iconPosition="right"
                  />
                </div>
              )}
          </>
        )}
        {isDeletionModalOpen && flight.benefitId && (
          <BenefitDeletionModal
            benefitId={flight.benefitId}
            closeModal={() => {
              setIsDeletionModalOpen(false);
            }}
            onDeleted={() =>
              navigate(
                PATHS.home.benefits.list +
                  getListSearchFromLocation(location, buildBenefitsListSearch),
              )
            }
          />
        )}
        {isDuplicationModalOpen && (
          <ModalDuplication
            actionOnClick={async (selectedFields) => {
              if (flight.benefitId) {
                const duplicatedFlight = await duplicateFlight({
                  benefitId: flight.benefitId,
                  fields: selectedFields.flatMap((fields) => fields.split(';')),
                });
                navigate(
                  PATHS.home.benefits.flight.view.replace(
                    ':benefitId',
                    duplicatedFlight.benefitId,
                  ),
                  { state: location.state },
                );
              }
            }}
            closeModal={() => {
              setIsDuplicationModalOpen(false);
            }}
            fields={getTranslatedConst(FLIGHT_DUPLICATION_FIELDS)}
            type="prestation"
            code={flight.benefit.code}
          />
        )}
      </StyledContainer>
    </FormProvider>
  );
};

export default ViewFlightForm;
